import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { icon } from '../lib/icons';
import { LightElement } from '../lib/light-element';

/**
 * Labelled password input with a show/hide toggle (spec §5.1). Renders a real
 * <input name=…> in light DOM, so the surrounding <form> sees it in FormData.
 */
@customElement('hl-password-field')
export class HlPasswordField extends LightElement {
  @property() label = 'Password';

  @property() name = 'password';

  @property({ attribute: 'input-id' }) inputId = 'password';

  @property() autocomplete = 'current-password';

  /** Small text right of the label, e.g. "Forgot it? Ask your admin." */
  @property() hint = '';

  /** Help text under the field. */
  @property() help = '';

  @property() error = '';

  @state() private visible = false;

  get input(): HTMLInputElement | null {
    return this.querySelector('input');
  }

  override focus(): void {
    this.input?.focus();
  }

  private toggle() {
    this.visible = !this.visible;
  }

  override render() {
    const errorId = `${this.inputId}-error`;
    const helpId = `${this.inputId}-help`;
    const describedBy = [this.error ? errorId : '', this.help ? helpId : ''].filter(Boolean).join(' ');
    return html`
      <div class="hl-field">
        <div class="hl-field-label-row">
          <label class="form-label" for=${this.inputId}>${this.label}</label>
          ${this.hint ? html`<span class="hl-field-hint">${this.hint}</span>` : nothing}
        </div>
        <div class="hl-password ${this.error ? 'is-invalid' : ''}">
          <input
            id=${this.inputId}
            name=${this.name}
            type=${this.visible ? 'text' : 'password'}
            autocomplete=${this.autocomplete}
            autocapitalize="none"
            spellcheck="false"
            aria-invalid=${this.error ? 'true' : 'false'}
            aria-describedby=${describedBy || nothing}
            data-testid="${this.name}-input"
          />
          <button
            type="button"
            class="btn btn-icon"
            aria-label=${this.visible ? 'Hide password' : 'Show password'}
            aria-pressed=${this.visible ? 'true' : 'false'}
            aria-controls=${this.inputId}
            @click=${this.toggle}
          >
            ${icon(this.visible ? 'eye-off' : 'eye')}
          </button>
        </div>
        ${this.help ? html`<div id=${helpId} class="form-text">${this.help}</div>` : nothing}
        ${this.error ? html`<div id=${errorId} class="hl-field-error">${this.error}</div>` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hl-password-field': HlPasswordField;
  }
}
